import type { ChangeEvent, KeyboardEvent } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { MAX_MESSAGE_LENGTH } from '~constants/dialogs';
import { RegisterInbound, RegisterOutbound } from '~features/annals';
import { annals } from '~features/annals/hooks';
import { useRequireAuth } from '~features/auth';
import { BotModelInfo } from '~features/botModel';
import { useHasSuggestedText } from '~features/tags';
// widgets shouldn't import features. Temp fix for circular dependency
import { TopicList } from '~features/topics/quarantine';
import { useCurrentBot } from '~hooks/useCurrentBot';
import { useSetLastBotId } from '~hooks/useLastBotId';
import { usePaidMessageSending } from '~hooks/usePaidMessageSending';
import { buildMessageFromString } from '~utils/messages';

import { SendButton } from './SendButton';
import { BotModelsArea, ConversationInputContainer } from './styles';
import { ConversationTextarea } from './TextArea';

type Props = {
	recipientId: string;
	userId: string;
	onSend: () => void;
};

export const ConversationInput = ({ recipientId, userId, onSend }: Props) => {
	const ref = useRef<{
		focus(): void;
		blur(): void;
	}>({ focus: () => undefined, blur: () => undefined });

	const [message, setMessage] = useState('');

	const { bot } = useCurrentBot({ recipientId });
	const setLastBotId = useSetLastBotId();
	const withRequireAuth = useRequireAuth();

	const { sendMessage, isLoading } = usePaidMessageSending();
	const hasSuggestedText = useHasSuggestedText();

	const annalsSendMsgKeyboard = annals.chat.useSendMsgKeyboard();

	const submit = withRequireAuth(
		() => {
			const sent = sendMessage({
				recipientId,
				id: userId,
				message: buildMessageFromString(message, userId),
			});

			if (sent) {
				ref.current.focus();
				onSend();
			}

			setMessage('');
			setLastBotId(recipientId);
		},
		{
			analytics: {
				inbound:
					bot.accountType === 'user-generated'
						? RegisterInbound.ChatSendMessageUDC
						: RegisterInbound.ChatSendMessage,
				outbound: RegisterOutbound.Chat,
				inboundParams: {
					botId: recipientId,
				},
			},
		}
	);

	const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
		setMessage(e.target.value);
	}, []);

	const disabled = useMemo(
		() =>
			isLoading ||
			message.trim().length === 0 ||
			message.length >= MAX_MESSAGE_LENGTH,
		[isLoading, message]
	);

	const handleKeyPress = useCallback(
		(e: KeyboardEvent<HTMLElement>) => {
			const len = message.trim().length;
			if (
				e.key === 'Enter' &&
				!e.shiftKey &&
				len > 0 &&
				len <= MAX_MESSAGE_LENGTH
			) {
				e.preventDefault();
				void annalsSendMsgKeyboard();
				submit();
			}
		},
		[annalsSendMsgKeyboard, message, submit]
	);

	return (
		<ConversationInputContainer>
			<ConversationTextarea
				ref={ref}
				value={message}
				onChange={handleChange}
				onKeyPress={handleKeyPress}
			/>
			<SendButton disabled={disabled} onSend={submit} />
			{hasSuggestedText === false && (
				<TopicList
					recipientId={recipientId}
					onClick={() => ref.current.focus()}
				/>
			)}
			<BotModelsArea className="bot-models-selector">
				<BotModelInfo recipientId={recipientId} />
			</BotModelsArea>
		</ConversationInputContainer>
	);
};
